// Form Table — recent-form standings over a venue + timeline window (Phase 3).
// Same shape as the League Table, so it reuses the shared <StandingsTable>; only
// the data source (venue + timeline) and controls differ. The ha/timeline
// values arrive via the bridge context from the legacy shell selects.

import { useMemo } from "react";
import { FORM_DEFAULT_HA, FORM_DEFAULT_TIMELINE, shapeLeagueRows } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import StandingsTable from "./StandingsTable.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface FormTableProps {
  fixture: string;
  /** Venue filter: "All" | "Home" | "Away". */
  ha?: string | null;
  /** Window: "5" | "10" | "Season". */
  timeline?: string | null;
}

export default function FormTable({ fixture, ha, timeline }: FormTableProps) {
  const haVal = ha ?? FORM_DEFAULT_HA;
  const tlVal = timeline ?? FORM_DEFAULT_TIMELINE;
  const { data, isPending, isError, refetch } = useWidgetData(
    "form-table",
    useFixtureKey(fixture)
  );
  // Full dataset keyed by `${ha}-${timeline}`; pick the active window
  // client-side (same lookup the embedded selector did) so toggles are instant.
  const teams =
    data?.[`${haVal}-${tlVal}`] ??
    data?.[`${FORM_DEFAULT_HA}-${FORM_DEFAULT_TIMELINE}`] ??
    [];

  const mobile = window.innerWidth <= 768;
  const rows = useMemo(
    () => shapeLeagueRows(teams, fixture, mobile),
    [teams, fixture, mobile]
  );

  if (isPending) return <WidgetSkeleton rows={6} />;
  if (isError) return <WidgetError onRetry={refetch} />;

  return (
    <StandingsTable rows={rows} resetKey={`${haVal}|${tlVal}|${fixture}`} />
  );
}
